import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid } from '@mui/material';
import { permissionsList, permissionValues } from 'common/constants';
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomDialog from 'components/dialogs/CustomDialog'
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect';
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput';
import { useAuth } from 'context/AuthContext';
import { useUsersContext } from 'context/UsersContext';
import { EPermission } from 'enum/EPermission';
import { useInviteUsers } from 'hooks/useUsers';
import InviteUserIcon from 'icons/InviteUserIcon'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form';
import { snackbarService } from 'services/snackbarService';
import { InviteUserDto } from 'types/dto/InviteUsersDto';
import * as yup from 'yup';

const initialValues = {
  email: '',
  permission: EPermission.EDITOR,
}

const validationSchema = yup.object({
  email: yup
    .string()
    .email('Invalid email address')
    .required("Email is required"),
  permission: yup
    .mixed<EPermission>()
    .oneOf(permissionValues)
    .required('Permission is required'),
});

export default function InviteUsersButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState<boolean>(false);
  const { refetch } = useUsersContext();

  const methods = useForm<InviteUserDto>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
      permission: EPermission.EDITOR,
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  const { mutate: inviteMutation, isLoading } = useInviteUsers(
    (data: any) => {
      if (data.status_code == 200) {
        const color = data?.data[0]?.includes('invited before') ? 'warning' : 'success';
        setOpen(false);
        snackbarService.showSnackbar(data?.data[0], color);
        refetch();
        return;
      }

      snackbarService.showSnackbar(data?.data[0], 'warning');
    },
    (err: any) => {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong, please try again';

      snackbarService.showSnackbar(message, 'error');
    }
  )

  const handleInvite = (values: InviteUserDto) => {
    inviteMutation({
      userId: user.id,
      dto: {
        users: [values]
      }
    })
  }

  const handlePress = () => {
    reset(initialValues);
    setOpen(true);
  }

  return (
    <FormProvider {...methods}>
      <CustomButton
        key='invite'
        text='Invite Users'
        color='primary'
        startIcon={<InviteUserIcon size={16} />}
        onClick={handlePress}
      />

      <CustomDialog
        open={open}
        title='Invite Users'
        message="Enter the user's email you want to invite and select their permission"
        content={
          <Box
            component='form'
            onSubmit={handleSubmit(handleInvite)}
          >

            <Grid container spacing={1} mt={3}>
              <Grid size={{ xs: 8 }}>
                <CustomFormTextInput
                  name='email'
                  register={register}
                  placeholder='Email address'
                  floatingLabel='Email address'
                  fullWidth
                  required
                  error={errors.email}
                />
              </Grid>

              <Grid size={{ xs: 4 }}>
                <CustomFormSelect
                  name='permission'
                  options={permissionsList}
                  placeholder='Permission'
                  floatingLabel='Permission'
                  fullWidth
                  required
                  error={errors.permission}
                />
              </Grid>
            </Grid>

            <Box my={1.5}>
              <CustomButton
                fullWidth
                text='Invite'
                color='primary'
                type='submit'
                loading={isLoading}
              />
            </Box>
          </Box>
        }
        onClose={() => setOpen(false)}
      />
    </FormProvider>
  )
}
